/**
 * Push Notification Opt-In Banner
 *
 * An unobtrusive banner that prompts users to enable push notifications.
 * Shows at the bottom of the screen and can be dismissed temporarily or permanently.
 */

import { useState } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { cn } from '@/lib/utils';

export function PushOptInBanner() {
  const { shouldShowPrompt, dismissPrompt, enable, isLoading, error } =
    usePushNotifications();
  const [isEnabling, setIsEnabling] = useState(false);
  const [showError, setShowError] = useState(false);

  if (!shouldShowPrompt) {
    return null;
  }

  const handleEnable = async () => {
    setIsEnabling(true);
    setShowError(false);
    const success = await enable();
    setIsEnabling(false);

    if (!success) {
      setShowError(true);
      // Auto-hide error after 5 seconds
      setTimeout(() => setShowError(false), 5000);
    }
  };

  const handleDismiss = () => {
    dismissPrompt(false); // Temporary dismiss (7 days)
  };

  const handleDismissPermanently = () => {
    dismissPrompt(true); // Permanent dismiss
  };

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
    >
      <div className="rounded-lg border bg-card shadow-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bell className="h-5 w-5 text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Enable Push Notifications</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Get notified about match results, challenges, and important updates even
                when you're not using the app.
              </p>

              {/* Error message */}
              {showError && error && (
                <p className="text-xs text-destructive mt-2">
                  {error === 'Notification permission denied'
                    ? 'Please allow notifications in your browser settings to continue.'
                    : error}
                </p>
              )}

              {/* Buttons */}
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleEnable}
                  disabled={isEnabling || isLoading}
                  className="h-8"
                >
                  {isEnabling ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Enabling...
                    </>
                  ) : (
                    'Enable'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="h-8 text-muted-foreground"
                  disabled={isEnabling}
                >
                  Not now
                </Button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismissPermanently}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 -mt-1 -mr-1"
              title="Don't show again"
              aria-label="Dismiss permanently"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

